import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound } from 'lucide-react';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SettingsPage() {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.new_password !== formData.confirm_password) {
            toast.error(language === 'zh' ? '两次输入的密码不一致' : 'Passwords do not match');
            return;
        }

        if (formData.new_password.length < 6) {
            toast.error(language === 'zh' ? '新密码至少需要6位' : 'Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API_URL}/auth/change-password`, {
                current_password: formData.current_password,
                new_password: formData.new_password
            });

            toast.success(language === 'zh' ? '密码修改成功' : 'Password changed successfully');
            setFormData({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (error) {
            const message = error.response?.data?.detail || (language === 'zh' ? '修改失败' : 'Failed to change password');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-layout" data-testid="settings-page">
            <Sidebar />
            <main className="content-container">
                <div className="p-6 md:p-8 max-w-2xl">
                    <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {language === 'zh' ? '账户设置' : 'Account Settings'}
                    </h1>

                    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                        <h3 className="flex items-center gap-2 text-lg font-semibold mb-6">
                            <KeyRound className="w-5 h-5 text-indigo-600" />
                            {language === 'zh' ? '修改密码' : 'Change Password'}
                        </h3>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>{language === 'zh' ? '当前密码' : 'Current Password'}</Label>
                                <Input
                                    type="password"
                                    value={formData.current_password}
                                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                                    required
                                    className="input-focus-ring"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>{language === 'zh' ? '新密码' : 'New Password'}</Label>
                                <Input
                                    type="password"
                                    value={formData.new_password}
                                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                                    required
                                    className="input-focus-ring"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>{language === 'zh' ? '确认新密码' : 'Confirm New Password'}</Label>
                                <Input
                                    type="password"
                                    value={formData.confirm_password}
                                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                                    required
                                    className="input-focus-ring"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-4"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'zh' ? '提交中...' : 'Saving...'}
                                    </>
                                ) : (
                                    language === 'zh' ? '修改密码' : 'Change Password'
                                )}
                            </Button>
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
}
